import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { TenantDetail } from '../api.js';
import { TENANT_TABS } from '../route.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { ErrorState, Skeleton } from '../ui/bits.js';
import { AccessTab } from './tenant/access.js';
import { ConnectTab } from './tenant/connect.js';
import { OverviewTab } from './tenant/overview.js';
import { SettingsTab } from './tenant/settings.js';
import { ToolsTab } from './tenant/tools.js';

/**
 * One site, as an object with parts.
 *
 * This was a single 300-line column: header, brand, health, faults, every
 * capability card in full, the widget previews, the view editor, the embed tag,
 * the access list and the kill switch, in that order, all mounted at once. Two
 * things were wrong with it and only one was the length. The other was that
 * none of it was addressable — "look at the Access list for kino-vela" was a
 * sequence of instructions rather than a link, the back button always landed at
 * the top of the same page, and every section paid its render cost on every
 * repaint of every other section.
 *
 * So the tenant is a destination with tabs, each of which is a slice of the
 * SAME payload already in `state.tenant`. There is no extra fetch behind any of
 * them; what changed is the URL and what is mounted.
 *
 * THE FAULTS ALERT STAYS ON THE SHELL. It is the one thing that has to be
 * visible from every tab: a capability that advertises a parameter its binding
 * cannot honour answers every question with the same row, confidently, and
 * `ok:true`, so no client will ever report it — which makes this banner the only
 * place it surfaces at all.
 */

function Shell(props: { tenantId: string; children?: unknown }): VNode {
  return html`
    <section class="wrap screen">
      <nav class="crumbs" aria-label="Breadcrumb">
        <a class="linkish" href="#/sites">← All sites</a>
      </nav>
      <h1 class="screen__h" tabindex="-1" data-focus-target>${props.tenantId}</h1>
      ${props.children}
    </section>
  `;
}

export function TenantScreen(props: {
  state: DashboardState;
  actions: DashboardActions;
  now: number;
  /** The origin this page talks to; the Connect tab builds its URLs on it. */
  origin: string;
}): VNode {
  const { state, actions, now, origin } = props;
  const id = state.tenantId ?? 'Site';

  if (state.error) {
    return Shell({
      tenantId: id,
      children: ErrorState({
        message: state.error,
        label: 'Could not load this site.',
        body: 'Nothing below is a fallback — this page has no recorded copy of a tenant to replay — so an unreachable API is this message and no screen.',
        onRetry: () => actions.reload(),
      }),
    });
  }

  if (!state.tenant) {
    return Shell({
      tenantId: id,
      children: Skeleton({ rows: 5, label: `Loading ${id}…` }),
    });
  }

  const t: TenantDetail = state.tenant;
  const faulty = t.capabilities.filter((c) => (t.issues[c.name] ?? []).length > 0);
  const base = `#/sites/${encodeURIComponent(t.tenantId)}`;

  const panel =
    // `appearance` is no longer a tab, but it was an address people could paste:
    // it opens the tools, which is where what it showed now lives.
    state.tenantTab === 'tools' || state.tenantTab === 'appearance'
      ? ToolsTab({ t, state, actions, now })
      : state.tenantTab === 'connect'
          ? ConnectTab({ t, state, actions, origin })
          : state.tenantTab === 'access'
            ? AccessTab({ tenantId: t.tenantId, state, actions, now })
            : state.tenantTab === 'settings'
              ? SettingsTab({ t, state, actions, now })
              : OverviewTab({ t, state, actions, now });

  return html`
    <section class="wrap screen">
      <nav class="crumbs" aria-label="Breadcrumb">
        <a class="linkish" href="#/sites">← All sites</a>
      </nav>

      <h1 class="screen__h" tabindex="-1" data-focus-target>${t.identity.displayName}</h1>
      <p class="screen__p">
        <code>${t.tenantId}</code>${SP}·${SP}${t.policy.ownership}${SP}·${SP}
        <a href=${t.source.canonicalOrigin} target="_blank" rel="noopener noreferrer">
          ${t.source.canonicalOrigin}
        </a>
        ${t.ownership
          ? html`${SP}·${SP}ownership ${t.ownership.state}
              ${t.ownership.domain ? html` for ${t.ownership.domain}` : null}
              ${t.ownership.method ? html` via ${t.ownership.method}` : null}`
          : html`${SP}·${SP}<span class="when when--never">no ownership claim on record</span>`}
      </p>

      ${faulty.length
        ? html`<div class="faults" role="alert">
            <p class="faults__h">
              ${faulty.length} ${faulty.length === 1 ? 'tool advertises' : 'tools advertise'}
              a parameter the binding cannot honour
            </p>
            <ul class="faults__list">
              ${faulty.map(
                (c) => html`<li>
                  <a href=${`${base}/tools`}><code>${c.name}</code></a>${SP}—${SP}
                  ${(t.issues[c.name] ?? [])[0]}
                </li>`,
              )}
            </ul>
            <p class="faults__p">
              Every one of these answers with rows and <code>ok:true</code> whatever it is passed,
              so no client will ever report it as broken.
            </p>
          </div>`
        : null}

      <nav class="tabs tabs--sub" aria-label=${`${t.identity.displayName} sections`}>
        ${TENANT_TABS.map((tab) => {
          const on = tab.tab === state.tenantTab;
          return html`<a
            class=${on ? 'tab tab--on' : 'tab'}
            href=${tab.tab === 'overview' ? base : `${base}/${tab.tab}`}
            aria-current=${on ? 'page' : undefined}
            >${tab.label}</a
          >`;
        })}
      </nav>

      ${panel}
    </section>
  `;
}
